import { isSubscriptionInterval, SubscriptionInterval } from 'constants/order';
import { isSubscriptionItem } from 'interfaces/line-item';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { gtm } from 'tracking';
import { Gtm } from 'tracking/gtm';
import { useCart } from '../../contexts/cart';

const PARAMS = 'addSingle';
const INTERVAL_PARAM = 'interval';

/**
 * Add items to cart by url params
 * url params: add=variantId,quantity,interval
 * @example ?add=1234567890,2,2week
 */
export const useAddItemsByUrl = () => {
  const { effectiveCart, isCartReady, addProductsToCart, addSubItemsToCart } =
    useCart();

  const { isReady, query, replace, pathname } = useRouter();
  const addItemsByUrlPerformed = useRef(false);
  const addedVariantIds = useRef<
    Array<{
      variantId: string;
      quantity: number;
      interval?: SubscriptionInterval;
    }>
  >([]);
  const [addedItems, setAddedItems] = useState<
    {
      productId: string;
      productTitle: string;
      variantTitle: string;
      variantId: string;
      productType: string;
    }[]
  >([]);

  /**
   * Add items to cart by url params
   */
  useEffect(() => {
    const addItems = Array.isArray(query['add'])
      ? query['add']
      : query['add']
        ? [query['add']]
        : [];
    const variantId = query[PARAMS] as string;
    const interval = query[INTERVAL_PARAM] as SubscriptionInterval;

    if (
      !isReady ||
      addItemsByUrlPerformed.current ||
      !isCartReady ||
      (addItems.length === 0 && !variantId)
    ) {
      return;
    }

    const productsToAdd: {
      variantId: string;
      quantity: number;
      interval?: SubscriptionInterval;
    }[] = [];

    // Handle legacy format: add=variantId,quantity,interval
    if (addItems?.length > 0) {
      productsToAdd.push(
        ...addItems.map((value) => {
          const [variantId, quantity = '1', interval] = value.split(',');
          const item = {
            variantId,
            quantity: parseInt(quantity, 10),
            interval: interval as SubscriptionInterval,
          };
          addedVariantIds.current.push(item);
          return item;
        })
      );
    }

    // Handle new format: addSingle=variantId&interval=interval
    if (query[PARAMS]) {
      const item = {
        variantId,
        quantity: 1,
        interval,
      };
      addedVariantIds.current.push(item);
      productsToAdd.push(item);
    }

    try {
      // Validate subscription intervals
      productsToAdd.forEach((product) => {
        if (product.interval && !isSubscriptionInterval(product.interval)) {
          throw new Error(`Invalid subscription interval: ${product.interval}`);
        }
      });
      if (productsToAdd.length > 0) {
        if (productsToAdd.some((product) => product.interval)) {
          addSubItemsToCart(productsToAdd);
        } else {
          addProductsToCart(productsToAdd);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      addItemsByUrlPerformed.current = true;
      const newQuery = { ...query };
      delete newQuery.add;
      delete newQuery[PARAMS];
      delete newQuery[INTERVAL_PARAM];
      replace(
        {
          pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      );
    }
  }, [
    isReady,
    query,
    pathname,
    replace,
    addProductsToCart,
    isCartReady,
    addSubItemsToCart,
  ]);

  /**
   * Track added items to cart
   */
  useEffect(() => {
    if (!effectiveCart || addedVariantIds.current.length === 0) return;

    const foundVariants = effectiveCart.items.filter((item) =>
      addedVariantIds.current.some(
        (variant) => variant.variantId === item.variantId + ''
      )
    );

    if (foundVariants.length > 0) {
      // Track each found variant
      foundVariants.forEach((variant) => {
        const item = {
          productId: variant.productId + '',
          productTitle: variant.productTitle,
          variantTitle: variant.title,
          variantId: variant.variantId + '',
          productType: variant.productType,
          sku: variant.sku,
          price: variant.price,
          tags: variant.tags,
          quantity:
            addedVariantIds.current.find(
              (id) => id.variantId === variant.variantId + ''
            )?.quantity || 1,
          interval: isSubscriptionItem(variant) ? variant.interval : undefined,
        };

        setAddedItems((prev) => [...prev, item]);

        gtm({
          group: Gtm.GroupName.Product,
          name: Gtm.ProductEventName.AddToCart,
          data: { item },
        });
      });
    }
    // Clear the tracked variant ids to ensure this only runs once
    addedVariantIds.current = [];
    addItemsByUrlPerformed.current = false;
  }, [effectiveCart]);

  return {
    addedItems,
  };
};
